import { Record as record, Map, List } from 'immutable';
import { v1 as uuidv1 } from 'uuid';
import _ from 'lodash';

import config from '../../../../src/config';
import trainTime from '../../../../src/lib/TrainTime';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import validators from '../../../../src/validation';

import {
    construct,
    fields,
    resolve,
    resolveMap
} from '../../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';

import Period from './Period';
import DayTickets from './DayTickets';
import DayEvidence from './DayEvidence';

const fieldSchema = {
    id: [fields.string],
    period: [resolve.as(Period)],
    dayTickets: [resolveMap.as(DayTickets), new Map()],
    dayEvidence: [resolveMap.as(DayEvidence), new Map()]
};

export default class CompensationTravelWeek extends record(
    getDefaults(fieldSchema)
) {
    constructor(values = {}) {
        super(_.defaults({}, values, { id: uuidv1() }));
    }

    static fromJSON(json = {}) {
        return construct(
            CompensationTravelWeek,
            json,
            getResolvers(fieldSchema)
        );
    }

    getComputedValues() {
        const validatesOwnSchema = model => model.isComplete();
        return {
            dayTickets: this.dayTickets.every(validatesOwnSchema),
            dayEvidence: this.dayEvidence.every(validatesOwnSchema),
            minTickets: this.hasEnoughTickets()
        };
    }

    getSchema() {
        return {
            dayTickets: validators.true(),
            dayEvidence: validators.true(),
            minTickets: validators.true()
        };
    }

    getPath(...pathParts) {
        return pathParts.map(
            part =>
                trainTime.isMoment(part) ? part.format('YYYY-MM-DD') : part
        );
    }

    setTickets(date, dayTickets) {
        const path = this.getPath('dayTickets', date);
        if (dayTickets) {
            return this.setIn(path, dayTickets).alignEvidenceWithTickets(
                date,
                dayTickets
            );
        } else {
            return this.deleteIn(path).deleteEvidence(date);
        }
    }

    getTickets(date) {
        return this.getIn(this.getPath('dayTickets', date));
    }

    hasTickets(date) {
        return this.hasIn(this.getPath('dayTickets', date));
    }

    getDaysWithTickets() {
        const { period } = this;
        return period
            ? period.getDays().filter(day => this.hasTickets(day))
            : new List();
    }

    getOtherDaysWithTickets(date) {
        return this.getDaysWithTickets().filter(day => !day.isSame(date));
    }

    isValidTicketType(type, date) {
        const otherDays = this.getOtherDaysWithTickets(date);
        if (otherDays.size === 0) {
            return true;
        } else if (type.weeklyExclusive) {
            return false;
        } else {
            return !this.hasExclusiveTicket(otherDays);
        }
    }

    canSaveTickets(date) {
        const maxTickets = config.get('compensationMaxTicketsPerWeek');
        const otherDays = this.getOtherDaysWithTickets(date);
        if (otherDays.size < maxTickets) {
            return !this.hasExclusiveTicket(otherDays);
        }
        return false;
    }

    getDaysSize() {
        return this.getDaysWithTickets().size;
    }

    hasEnoughTickets() {
        const minTickets = config.get('compensationMinTicketsPerWeek');
        const daysWithTickets = this.getDaysWithTickets();
        return (
            daysWithTickets.size >= minTickets ||
            this.hasExclusiveTicket(daysWithTickets)
        );
    }

    hasExclusiveTicket(listOfDates = this.getDaysWithTickets()) {
        return listOfDates.some(d => this.getTickets(d).hasWeeklyExclusive());
    }

    hasLevel2Tickets() {
        return this.dayTickets.some(dayTickets => dayTickets.isLevel2());
    }

    getEvidence(date) {
        return this.getIn(this.getPath('dayEvidence', date));
    }

    setEvidence(date, dayEvidence) {
        return this.setIn(this.getPath('dayEvidence', date), dayEvidence);
    }

    deleteEvidence(date) {
        return this.deleteIn(this.getPath('dayEvidence', date));
    }

    alignEvidenceWithTickets(date, dayTickets) {
        const dayEvidence = (this.getEvidence(date) || new DayEvidence())
            .setTicketType('outward', dayTickets.getTicketType('outward'))
            .setTicketType('return', dayTickets.getTicketType('return'));
        return this.setEvidence(date, dayEvidence);
    }

    getRoutes() {
        return this.dayTickets
            .toList()
            .flatMap(dayTicket => dayTicket.getRoutes());
    }

    getOysterCards() {
        return this.dayTickets
            .toList()
            .flatMap(dayTicket => dayTicket.getOysterCards());
    }

    serializeClaims() {
        return this.getDaysWithTickets()
            .flatMap(date => {
                const dayTickets = this.getTickets(date);
                const dayEvidence = this.getEvidence(date);
                const makeClaimDetails = direction =>
                    dayTickets.get(direction)
                        ? {
                              date,
                              ticket: dayTickets.get(direction),
                              evidence: dayEvidence.get(direction)
                          }
                        : null;
                return List.of(
                    makeClaimDetails('outward'),
                    makeClaimDetails('return')
                ).filter(Boolean);
            })
            .map(({ date, ticket, evidence }) =>
                ticket.asClaim({ date, evidence })
            );
    }

    hasUpload() {
        return this.dayEvidence.some(evidence => evidence.hasUpload());
    }
}

Object.assign(CompensationTravelWeek.prototype, ValidationMixin);
